import { AvatarRenderer } from "@faire/deprecated-retailer-visitor-shared/components/LayoutElementRenderer/__internal__/AvatarRenderer";
import { Context } from "@faire/deprecated-retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Context";
import { useLayoutElementCarouselCtaLink } from "@faire/deprecated-retailer-visitor-shared/components/LayoutElementRenderer/__internal__/useLayoutElementCarouselCtaLink";
import { TitleStyles } from "@faire/deprecated-retailer-visitor-shared/components/LayoutElementRenderer/styles/TitleStyles";
import { FaireMarketIcon } from "@faire/deprecated-retailer-visitor-shared/features/FaireMarkets/FaireMarketIcon";
import {
  showMarketTreatment,
  MY_MARKET_LIST_CAROUSEL,
} from "@faire/deprecated-retailer-visitor-shared/views/home/logged-in-homepage/Utils";
import { ILayoutElement } from "@faire/web-api--source/indigofair/data/ILayoutElement";
import { useStrictLocalization } from "@faire/web/common/localization";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Button } from "@faire/web/slate-deprecated/Button";
import { IconButton } from "@faire/web/slate-deprecated/IconButton";
import { Arrow } from "@faire/web/slate/icons/foundation/Arrow";
import { Column, Flex } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { eventsThemeColors } from "@faire/web/slate/Theme/eventsThemeColors";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Link } from "@faire/web/ui/LinkAnchor";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import * as React from "react";
import styled, { css } from "styled-components";

export interface LayoutElementCarouselTitleProps {
  className?: string;
  leftAlignTitle?: boolean;
  showArrowCTA?: boolean;
  showPrimaryBtn?: boolean;
  hideTitle?: boolean;
  titleClassName?: string;
}

export namespace LayoutElementCarouselTitleProps {
  export const build = (
    partial?: Partial<LayoutElementCarouselTitleProps> | undefined
  ): LayoutElementCarouselTitleProps => ({
    ...(partial ?? {}),
  });
}

const AuxiliaryTitle: React.FC<{
  showArrowCTA?: boolean;
  showPrimaryBtn?: boolean;
}> = (props) => {
  const { strictLocalize } = useStrictLocalization();
  const { layoutElement, onCTAClick, requestId } = React.useContext(Context);
  const { showArrowCTA, showPrimaryBtn } = props;
  const {
    cta_link_to_search_products,
    cta_link_to_search_makers,
    cta_link,
    cta_text,
    on_click_event_name,
  } = layoutElement;

  const ctaLink = useLayoutElementCarouselCtaLink(layoutElement, requestId);
  const showAuxiliaryTitleInternal =
    !!cta_link || !!cta_link_to_search_products || !!cta_link_to_search_makers;
  if (!showAuxiliaryTitleInternal) {
    return null;
  }
  const auxiliaryTitle = cta_text
    ? cta_text
    : strictLocalize({
        id: "v/Z/3h",
        defaultMessage: "See All",
        description: {
          text: "Prompt to see all elements in a list",
        },
      });
  const desktopAuxiliaryTitle = <>{auxiliaryTitle}</>;

  if (showPrimaryBtn) {
    return (
      <StyledButton
        linkTo={ctaLink}
        onClick={() => onCTAClick?.(on_click_event_name ?? "", requestId)}
      >
        {cta_text}
      </StyledButton>
    );
  }

  const shouldShowMarketTreatment = showMarketTreatment(on_click_event_name);
  return (
    <TitleStyles.AuxiliaryTitle forwardedAs="span">
      {ctaLink ? (
        <Button
          linkTo={ctaLink}
          variant="tertiary"
          tertiaryColor={
            shouldShowMarketTreatment
              ? eventsThemeColors.faireMarket.promoText
              : undefined
          }
          onClick={() => {
            onCTAClick?.(on_click_event_name ?? "", requestId);
          }}
        >
          <TitleStyles.AuxiliaryTitleArrow $isVisible={!!showArrowCTA}>
            <StyledIconButton
              variant="tertiary"
              aria-label={auxiliaryTitle}
              $varArrowColor={layoutElement.main_text_color ?? "inherit"}
            >
              <Arrow
                data-test-id="arrow-cta"
                direction="right"
                fontSize="16px"
              />
            </StyledIconButton>
          </TitleStyles.AuxiliaryTitleArrow>
          <TitleStyles.AuxiliaryTitleText $isVisible={!showArrowCTA}>
            {desktopAuxiliaryTitle}
          </TitleStyles.AuxiliaryTitleText>
        </Button>
      ) : (
        cta_text
      )}
    </TitleStyles.AuxiliaryTitle>
  );
};

interface ICarouselDescriptionProps {
  descriptionText: string | undefined;
  descriptionCtaText: string | undefined;
  descriptionCtaLink: string | undefined;
  descriptionTextColor: string | undefined;
  leftAlign: boolean | undefined;
}

const CarouselDescription: React.FC<ICarouselDescriptionProps> = ({
  descriptionText,
  descriptionCtaText,
  descriptionCtaLink,
  descriptionTextColor,
  leftAlign,
}) => {
  if (!descriptionText) {
    return null;
  }

  const getDescriptionLink = () => {
    if (descriptionCtaText && descriptionCtaLink) {
      return (
        <Button
          variant="tertiary"
          linkTo={descriptionCtaLink}
          target="_blank"
          tertiaryColor={descriptionTextColor}
          style={{ paddingLeft: getSpacing("1x") }}
        >
          {descriptionCtaText}
        </Button>
      );
    }
    return null;
  };

  return (
    <TitleStyles.Subtitle $leftAlignTitle={leftAlign}>
      {descriptionText}
      {getDescriptionLink()}
    </TitleStyles.Subtitle>
  );
};

export const Title = ({
  className,
  leftAlignTitle,
  showArrowCTA,
  showPrimaryBtn,
  titleClassName,
}: LayoutElementCarouselTitleProps) => {
  const { layoutElement, requestId, onCTAClick } = React.useContext(Context);

  const {
    main_text,
    tagline_text,
    description_text,
    cta_text,
    on_click_event_name,
    display_page,
    main_text_color,
    cta_text_color,
    tagline_text_color,
  } = layoutElement;
  const link = useLayoutElementCarouselCtaLink(layoutElement, requestId);

  if (
    [main_text, tagline_text, description_text, cta_text].some(isNotUndefined)
  ) {
    const shouldShowMarketTreatment = showMarketTreatment(on_click_event_name);
    const noMarketBackground = on_click_event_name === MY_MARKET_LIST_CAROUSEL;
    const mainTextColor =
      shouldShowMarketTreatment && !noMarketBackground
        ? eventsThemeColors.faireMarket.promoText
        : main_text_color;

    const ctaTextColor =
      shouldShowMarketTreatment && !noMarketBackground
        ? eventsThemeColors.faireMarket.promoText
        : cta_text_color;
    const mainTitle = (
      <TitleStyles.MainTitleRow gap="1x" data-show-arrow-cta={showArrowCTA}>
        {shouldShowMarketTreatment ? (
          <ResponsiveContentV2
            tabletAndAbove={<FaireMarketIcon size="32px" />}
            mobile={<MobileFaireMarketIcon size="24px" />}
          />
        ) : null}
        <TitleStyles.MainTitle
          $leftAlignTitle={leftAlignTitle}
          maxLines={
            display_page === ILayoutElement.DisplayPage.FAIRE_MARKET_HOMEPAGE_V2
              ? 3
              : 2
          }
          color={mainTextColor}
        >
          {layoutElement.main_text}
        </TitleStyles.MainTitle>
      </TitleStyles.MainTitleRow>
    );
    return (
      <TitleStyles.Container
        className={className}
        $showMarketTreatment={shouldShowMarketTreatment && !noMarketBackground}
      >
        <TitleStyles.HeaderTaglineWrapper>
          <AvatarRenderer layoutElement={layoutElement} />
          <TitleStyles.Header
            $leftAlignTitle={leftAlignTitle}
            color={tagline_text_color}
          >
            {tagline_text}
          </TitleStyles.Header>
        </TitleStyles.HeaderTaglineWrapper>
        <TitleContentWrapper
          className={titleClassName}
          $mobileAlignInColumn={showPrimaryBtn}
        >
          <Column
            gap={{
              mobileAndAbove: 0,
              tabletAndAbove: 8,
            }}
          >
            <ResponsiveContentV2
              tabletAndAbove={mainTitle}
              mobile={
                cta_text && link ? (
                  <Link
                    to={link}
                    color={ctaTextColor}
                    onClick={() => {
                      onCTAClick?.(
                        layoutElement.on_click_event_name ?? "",
                        requestId
                      );
                    }}
                  >
                    {mainTitle}
                  </Link>
                ) : (
                  mainTitle
                )
              }
            />
            <CarouselDescription
              descriptionText={layoutElement.description_text}
              descriptionCtaText={layoutElement.description_cta_text}
              descriptionCtaLink={layoutElement.description_cta_link}
              descriptionTextColor={layoutElement.description_text_color}
              leftAlign={leftAlignTitle}
            />
          </Column>
          <AuxiliaryTitle
            showArrowCTA={showArrowCTA}
            showPrimaryBtn={showPrimaryBtn}
          />
        </TitleContentWrapper>
      </TitleStyles.Container>
    );
  }
  return null;
};

const StyledIconButton = styled(IconButton)<{ $varArrowColor?: string }>`
  & > div {
    color: ${({ $varArrowColor }) => $varArrowColor} !important;
  }
`;

const MobileFaireMarketIcon = styled(FaireMarketIcon)`
  margin-top: 4px;
`;

const StyledButton = styled(Button)`
  width: 261px;

  ${responsiveMediaQueries.tabletAndAbove} {
    width: 318px;
  }
`;

const TitleContentWrapper = styled(Flex).attrs({
  gap: "1x",
  justify: "space-between",
})<{ $mobileAlignInColumn?: boolean }>`
  ${responsiveMediaQueries.desktopAndAbove} {
    flex-wrap: wrap;
  }

  ${responsiveMediaQueries.mobile} {
    ${(props) =>
      props.$mobileAlignInColumn
        ? // eslint-disable-next-line @faire/prefer-slate-attrs -- move direction to a prop
          css`
            flex-direction: column;
          `
        : ""}
  }
`;
